import { useFormContext } from 'react-hook-form';
import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  FormControlLabel,
  Menu,
  MenuItem,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import Button from '@mui/material/Button';
import type { PopupState as PopupStateT } from 'material-ui-popup-state/hooks';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import type { SelectProps } from 'components/common';
import { Autocomplete, Select, SuspenseErrorFallback } from 'components/common';
import { useLocationParams } from 'hooks';
import { useAdTypes } from '../api/getAdTypes';
import type { AccountEntry, CampaignEntry, ConversionDomainEntry, RowStatus } from '../api';
import { useAccounts, useConversionDomain } from '../api';
import { useCallToActions } from '../api/';
import type { RowResultEntry } from '../api/types';
import type { AdSetEntry } from '../api/getAdSets';
import type { PackEntry } from 'types/shared';
import { isFacebookAdTypeCode } from '../constants';

type SelectAdTypes = Pick<SelectProps, 'handleChange'>;
export const SelectAdTypes: FC<SelectAdTypes> = ({ handleChange }) => {
  const { watch, setValue } = useFormContext();
  const { setAdType: setAdTypeParam } = useLocationParams();
  const platform = watch('platform') as string;
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number;
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const hasCreatives = (packsWithCreatives?.[activePacksWithCreatives]?.creatives?.length ?? 0) > 0;

  const { data, isLoading } = useAdTypes({
    params: { platform },
    config: {
      enabled: !!platform,
      select: (data) =>
        data.map((item) => ({
          value: item.code,
          label: item.name,
        })),
    },
  });

  const onAdTypeChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const nextValue = String(event.target.value ?? '');
      setAdTypeParam(isFacebookAdTypeCode(nextValue) ? nextValue : undefined);
      handleChange?.(event);
    },
    [handleChange, setAdTypeParam],
  );

  return isLoading ? (
    <Skeleton
      height={40}
      sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
    />
  ) : (
    <Select
      name={`packsWithCreatives.${activePacksWithCreatives}.adType`}
      inputLabel="Ad type"
      disabled={isEmpty(data)}
      options={data ?? []}
      handleChange={onAdTypeChange}
      autoFocus
      confirmation={hasCreatives ? {
        title:
          'Changing Ad type will reset selected creatives for the current pack',
        bodyText:
          'Changing the Ad type will reset selected creatives for this pack',
        cancelButtonTitle: 'Cancel',
        submitButtonTitle: 'Change Ad Type',
        onSubmit: () => {
          setValue(`packsWithCreatives.${activePacksWithCreatives}.creatives`, []);
          setValue('selectedCreatives', []);
        },
      } : undefined}
    />
  );
};

export const SelectAdAccounts = () => {
  const { watch } = useFormContext();
  const app = watch('app') as string;
  const platform = watch('platform') as string;

  const dataMapper = (item: AccountEntry) => ({
    value: item.ad_account_id,
    label: item.name,
  });

  const checkIsActive = (item: AccountEntry) => item.is_active;

  const { data, isError, error } = useAccounts({
    params: { platform, app },
    config: {
      select: (data) => data.filter(checkIsActive).map(dataMapper),
    },
  });

  if (isError) {
    return (
      <SuspenseErrorFallback
        title={'Ad Accounts'}
        message={
          (
            error as {
              cause?: { error?: string };
            }
          )?.cause?.error ?? 'An error occurred while Ad Accounts'
        }
      />
    );
  }

  return (
    <Autocomplete
      id="adAccounts"
      name="adAccounts"
      InputProps={{
        label: 'Ad Accounts',
      }}
      options={data ?? []}
    />
  );
};

export const SelectCallToActions = () => {
  const { watch } = useFormContext();
  const platform = watch('platform') as string;

  const { data, isLoading, isError, error } = useCallToActions({
    params: { platform },
    config: {
      enabled: !!platform,
      select: (data) =>
        data.map((item) => ({
          value: item.code,
          label: item.name,
        })),
    },
  });

  if (isError) {
    return (
      <SuspenseErrorFallback
        title={'Call To Actions'}
        message={
          (error as { cause?: { error?: string } })?.cause?.error ??
          'Error occur'
        }
      />
    );
  }

  return isLoading ? (
    <Skeleton
      height={40}
      sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
    />
  ) : (
    <Select
      dataTestId={'callToActions'}
      name="callToActions"
      inputLabel="Call To Actions"
      disabled={isEmpty(data)}
      options={data ?? []}
    />
  );
};

export const SelectConversionDomain = () => {
  const { setValue, watch } = useFormContext();
  const app = watch('app') as string;

  const { data, isError, error, isSuccess } = useConversionDomain({
    params: { app },
  });

  useEffect(() => {
    if (isSuccess) {
      const defaultDomain = data?.find((entry) => entry.is_default);
      if (defaultDomain) {
        setValue('selectedConversionDomain', defaultDomain.domain, {
          shouldValidate: true,
        });
      }
    }
  }, [setValue, isSuccess, data]);

  if (isError) {
    return (
      <SuspenseErrorFallback
        title={'Conversion domain'}
        message={
          (error as { cause?: { error?: string } })?.cause?.error ??
          'Error occur'
        }
      />
    );
  }

  const options = data?.map((item: ConversionDomainEntry) => ({
    value: item.domain,
    label: item.domain,
  }));

  return (
    <Select
      dataTestId={'selectedConversionDomain'}
      name="selectedConversionDomain"
      inputLabel="Conversion Domain"
      required
      disabled={isEmpty(data)}
      options={options ?? []}
    />
  );
};

export const DuplicateStatusMenu = ({
  formField,
}: {
  formField: 'selectedCampaigns' | 'selectedAdSets';
}) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'duplicateStatusMenu',
  });
  const { watch, setValue } = useFormContext();
  const selectedRows = watch(formField) as (CampaignEntry | AdSetEntry)[];

  const onSetAllStatus = useCallback(
    (popupState: PopupStateT, status: RowStatus) => {
      setValue(
        formField,
        selectedRows.map((c) => ({ ...c, status }))
      );
      popupState.close(); // Close the menu
    },
    [setValue, formField, selectedRows]
  );

  return (
    <>
      <Button
        {...bindTrigger(popupState)}
        endIcon={<ArrowDropDownIcon />}
        color={'inherit'}
        variant={'text'}
        size={'small'}
        sx={{ textTransform: 'initial', fontSize: 14 }}
      >
        Duplicate status
      </Button>
      <Menu {...bindMenu(popupState)}>
        <MenuItem
          onClick={() => {
            onSetAllStatus(popupState, 'ACTIVE');
          }}
        >
          Set all as Active
        </MenuItem>
        <MenuItem
          onClick={() => {
            onSetAllStatus(popupState, 'PAUSED');
          }}
        >
          Set all as Paused
        </MenuItem>
      </Menu>
    </>
  );
};

export const CampaignStatusSwitcher = ({
  cellParams,
}: {
  cellParams: GridRenderCellParams<CampaignEntry>;
}) => {
  const currentId = cellParams.row.id;
  const { watch, setValue } = useFormContext();
  const selectedCampaignsRows = watch('selectedCampaigns') as CampaignEntry[];
  const currentRow = selectedCampaignsRows?.find((r) => r.id === currentId);
  const isSelected = !!currentRow;
  const isChecked = currentRow?.status === 'ACTIVE';
  const labelText = isChecked ? 'Active' : 'Paused';

  const onChange = useCallback(
    (_: ChangeEvent, checked: boolean) => {
      setValue(
        'selectedCampaigns',
        selectedCampaignsRows.map((campaign) => {
          if (campaign.id === currentId) {
            return { ...campaign, status: checked ? 'ACTIVE' : 'PAUSED' };
          }
          return campaign;
        })
      );
    },
    [currentId, setValue, selectedCampaignsRows]
  );

  return (
    isSelected && (
      <FormControlLabel
        onClick={(e) => {
          e.stopPropagation();
        }}
        control={
          <Switch
            size={'small'}
            checked={isChecked}
            onChange={onChange}
            sx={{ mr: 1 }}
          />
        }
        label={labelText}
        sx={{ pl: 2 }}
      />
    )
  );
};

export const JobResultTable = ({ rows }: { rows?: RowResultEntry[] }) => {
  if (!rows)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: '500' }} gutterBottom>
          Job Result
        </Typography>
        <Typography
          sx={(t) => ({ fontSize: 16, color: t.palette.error.main, mb: 2 })}
          gutterBottom
        >
          An error occurred while fetching data
        </Typography>
      </Box>
    );

  return (
    <TableContainer sx={{ width: '100%' }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell align="center">Ad Account</TableCell>
            <TableCell align="center">Adset</TableCell>
            <TableCell align="center">Creative</TableCell>
            <TableCell align="center">Result</TableCell>
            <TableCell align="center">Ad ID</TableCell>
            <TableCell align="center">Message</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: RowResultEntry) => (
            <TableRow
              key={row.name}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {row.account_id}
              </TableCell>
              <TableCell align="left">{row.ad_set}</TableCell>
              <TableCell align="left">{row.creative}</TableCell>
              <TableCell align="center">{row.success ? '✅' : '❌'}</TableCell>
              <TableCell align="center">{row.ad_id}</TableCell>
              <TableCell align="left">{row.output}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
